import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import ASC_LOGO from '../../../assets/asc-logo.jpg'
import { getKioskIdentity, sendKioskHeartbeat } from '../../../services/KioskAuthService'
import KioskAttendanceActions from './KioskAttendanceActions'
import KioskLogoutDialog from './KioskLogoutDialog'

// Matches the backend's 90s online timeout (kiosk_ticket.md KIOSK-014) with room to spare.
const HEARTBEAT_INTERVAL_MS = 45000

export default function KioskReadyScreen() {
    const [kiosk, setKiosk] = useState(null)
    const [loading, setLoading] = useState(true)
    const [currentTime, setCurrentTime] = useState(new Date())
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false)
    const navigate = useNavigate()

    const loadIdentity = useCallback(async () => {
        const result = await getKioskIdentity()
        if (result.error) {
            navigate('/kiosk/login')
            return
        }
        setKiosk(result)
        setLoading(false)
    }, [navigate])

    useEffect(() => {
        if (!localStorage.getItem('kioskAccessToken')) {
            navigate('/kiosk/login')
            return
        }
        loadIdentity()
    }, [loadIdentity, navigate])

    useEffect(() => {
        const interval = setInterval(() => {
            sendKioskHeartbeat()
        }, HEARTBEAT_INTERVAL_MS)

        return () => clearInterval(interval)
    }, [])

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000)
        return () => clearInterval(timer)
    }, [])

    const formattedTime = currentTime.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
    })
    const formattedDate = currentTime.toLocaleDateString([], {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen w-full bg-neutral-50">
                <img src={ASC_LOGO} alt="ASC Logo" className="h-10 opacity-60" />
            </div>
        )
    }

    return (
        <div className="flex flex-col items-center justify-center min-h-screen w-full bg-neutral-50 text-center px-6 py-10">
            <img src={ASC_LOGO} alt="ASC Logo" className="h-10 mb-10" />

            <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-2">{formattedDate}</p>
            <h2 className="text-7xl font-light text-gray-900 tabular-nums mb-3">{formattedTime}</h2>

            <p className="text-base font-medium text-gray-600">{kiosk?.name}</p>
            <p className="text-sm text-gray-400 mb-10">
                {kiosk?.kiosk_code} &middot; {kiosk?.location}
            </p>

            <KioskAttendanceActions />

            <button
                onClick={() => setLogoutDialogOpen(true)}
                className="text-xs text-gray-400 hover:text-red-600 mt-12 py-3 px-4 transition-colors"
            >
                Log out this kiosk
            </button>

            <KioskLogoutDialog
                open={logoutDialogOpen}
                onClose={() => setLogoutDialogOpen(false)}
                onLoggedOut={() => navigate('/kiosk/login')}
                kioskCode={kiosk?.kiosk_code}
            />
        </div>
    )
}
